import { useStore } from '@nanostores/react';
import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { acceptInvitation, declineInvitation } from '~/lib/.client/api/organizations';
import { trackTeamMemberInvitationAccepted, trackTeamMemberInvitationDeclined } from '~/lib/analytics/team-membership';
import { WINDOW_RELOAD_TIMEOUT } from '~/lib/constants';
import { userStore } from '~/lib/stores/user';
import type { OrganizationInvite } from '~/types/organization';
import { withSpinner } from '~/utils/withSpinner';

interface UseTeamInvitesOptions {
  onAccept?: (invite: OrganizationInvite) => void;
  onDeny?: (invite: OrganizationInvite) => void;
}

export function useTeamInvites({ onAccept, onDeny }: UseTeamInvitesOptions) {
  const user = useStore(userStore.user);
  const invites = user?.organizationInvites || [];

  const [dismissed, setDismissed] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDenying, setIsDenying] = useState(false);

  const invite = invites?.[0] || null;

  const handleAccept = useCallback(async () => {
    if (!invite) {
      return;
    }

    await withSpinner({
      action: async () => {
        try {
          await acceptInvitation({ token: invite.token });
          setAccepted(true);

          // track invitation acceptance
          const currentUser = userStore.getUser();
          trackTeamMemberInvitationAccepted(invite.organization, undefined, currentUser);

          onAccept?.(invite);

          setTimeout(() => window.location.reload(), WINDOW_RELOAD_TIMEOUT);
        } catch (error) {
          console.error(error);

          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }
        }
      },
      onStateUpdate: setIsAccepting,
    });
  }, [invite, onAccept]);

  const handleDeny = useCallback(async () => {
    if (!invite) {
      return;
    }

    await withSpinner({
      action: async () => {
        try {
          await declineInvitation({ token: invite.token });
          userStore.removeOrganizationInvite(invite.token);

          // track invitation decline
          const currentUser = userStore.getUser();
          trackTeamMemberInvitationDeclined(invite.organization, undefined, currentUser);

          onDeny?.(invite);
        } catch (error) {
          console.error(error);

          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }
        }
      },
      onStateUpdate: setIsDenying,
    });
  }, [invite, onDeny]);

  const dismiss = useCallback(() => setDismissed(true), []);

  return {
    invite,
    isAccepting,
    isDenying,
    accepted,
    dismissed,
    accept: handleAccept,
    deny: handleDeny,
    dismiss,
  };
}
